import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { updatePlayer } from 'store/auth'
import HostBadgeIcon from 'assets/icons/host-badge.svg'
import {
  AVATAR_NAMES,
  DEFAULT_AVATAR,
  getAvatarSrc,
  LOBBY_NAME_MAX_LENGTH,
  clampLobbyNameInput,
  PLAYER_ROLES
} from './constants'

function PlayerProfile({ currentPlayer, currentTeam, takenRolesExcludingSelf, isGameCreator }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [editPlayer, setEditPlayer] = useState(false)
  const [editNickname, setEditNickname] = useState('')
  const [editRole, setEditRole] = useState('')
  const [editAvatar, setEditAvatar] = useState('')
  const [savingPlayer, setSavingPlayer] = useState(false)

  const displayAvatar =
    currentPlayer?.avatar && AVATAR_NAMES.includes(currentPlayer.avatar)
      ? currentPlayer.avatar
      : DEFAULT_AVATAR

  const startEditPlayer = useCallback(() => {
    setEditNickname(currentPlayer?.alias ?? '')
    setEditRole(currentPlayer?.game_role ?? '')
    setEditAvatar(
      currentPlayer?.avatar && AVATAR_NAMES.includes(currentPlayer.avatar)
        ? currentPlayer.avatar
        : DEFAULT_AVATAR
    )
    setEditPlayer(true)
  }, [currentPlayer])

  const cycleAvatar = (direction) => {
    const idx = AVATAR_NAMES.indexOf(editAvatar)
    const next =
      direction === 'prev'
        ? (idx - 1 + AVATAR_NAMES.length) % AVATAR_NAMES.length
        : (idx + 1) % AVATAR_NAMES.length
    setEditAvatar(AVATAR_NAMES[next])
  }

  const handleSavePlayer = useCallback(async () => {
    if (!currentPlayer) return
    const aliasTrimmed = editNickname.trim()
    if (aliasTrimmed.length > LOBBY_NAME_MAX_LENGTH) return
    setSavingPlayer(true)
    try {
      await dispatch(
        updatePlayer({
          alias: aliasTrimmed,
          game_role: editRole || null,
          avatar: editAvatar
        })
      )
      setEditPlayer(false)
    } catch (err) {
      const status = err.response?.status
      const data = err.response?.data
      const msg =
        status === 401
          ? t('lobby.session_expired')
          : data?.errors?.join?.(' ') || data?.message || t('lobby.role_taken')
      toast.error(msg)
    } finally {
      setSavingPlayer(false)
    }
  }, [dispatch, currentPlayer, editNickname, editRole, editAvatar, t])

  return (
    <div className="lobby__current-player">
      {!editPlayer ? (
        <div className="lobby__current-player-display">
          <div className="lobby__current-player-avatar">
            <img src={getAvatarSrc(displayAvatar)} alt="" className="lobby__avatar-img" />
          </div>
          <div className="lobby__current-player-info">
            <span className="lobby__current-player-nickname">
              {currentPlayer?.alias || currentPlayer?.email || ''}
              {isGameCreator && (
                <img src={HostBadgeIcon} alt="" className="lobby__host-badge" title="Host" />
              )}
            </span>
            <span className="lobby__team-player-role">
              {currentPlayer?.game_role ? currentPlayer.game_role.toUpperCase() : '-'}
            </span>
          </div>
          {currentPlayer && currentTeam && (
            <button type="button" className="lobby__edit-player-btn" onClick={startEditPlayer}>
              {t('lobby.edit_player')}
            </button>
          )}
        </div>
      ) : (
        <div className="lobby__current-player-form">
          <div className="lobby__avatar-chooser">
            <button
              type="button"
              className="lobby__avatar-arrow"
              onClick={() => cycleAvatar('prev')}
              aria-label="Previous avatar"
            >
              <i className="fa-solid fa-chevron-left" />
            </button>
            <div className="lobby__current-player-avatar lobby__current-player-avatar--large">
              <img src={getAvatarSrc(editAvatar)} alt="" className="lobby__avatar-img" />
            </div>
            <button
              type="button"
              className="lobby__avatar-arrow"
              onClick={() => cycleAvatar('next')}
              aria-label="Next avatar"
            >
              <i className="fa-solid fa-chevron-right" />
            </button>
          </div>
          <div className="lobby__avatar-grid">
            <div className="lobby__avatar-row">
              {AVATAR_NAMES.slice(0, 3).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`lobby__avatar-option ${
                    editAvatar === key ? 'lobby__avatar-option--selected' : ''
                  }`}
                  onClick={() => setEditAvatar(key)}
                  aria-label={`Avatar ${AVATAR_NAMES.indexOf(key) + 1}`}
                >
                  <img src={getAvatarSrc(key)} alt="" className="lobby__avatar-img" />
                </button>
              ))}
            </div>
            <div className="lobby__avatar-row">
              {AVATAR_NAMES.slice(3, 6).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`lobby__avatar-option ${
                    editAvatar === key ? 'lobby__avatar-option--selected' : ''
                  }`}
                  onClick={() => setEditAvatar(key)}
                  aria-label={`Avatar ${AVATAR_NAMES.indexOf(key) + 1}`}
                >
                  <img src={getAvatarSrc(key)} alt="" className="lobby__avatar-img" />
                </button>
              ))}
            </div>
          </div>
          <div
            className={`lobby__form-labels ${
              editNickname.trim() && editRole ? 'lobby__form-labels--filled' : ''
            }`}
          >
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="lobby__form-label">
              {t('lobby.nickname')}
              <input
                type="text"
                className="lobby__form-input"
                value={editNickname}
                maxLength={LOBBY_NAME_MAX_LENGTH}
                onChange={(e) =>
                  setEditNickname(clampLobbyNameInput(editNickname, e.target.value))
                }
              />
            </label>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="lobby__form-label">
              {t('lobby.role')}
              <select
                className={`lobby__form-select ${!editRole ? 'lobby__form-select--placeholder' : ''}`}
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
              >
                <option value="">Select Role</option>
                {PLAYER_ROLES.map((r) => (
                  <option
                    key={r.value}
                    value={r.value}
                    disabled={takenRolesExcludingSelf.includes(r.value)}
                  >
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            type="button"
            className="lobby__save-player-btn"
            onClick={handleSavePlayer}
            disabled={
              savingPlayer ||
              editNickname.trim().length > LOBBY_NAME_MAX_LENGTH
            }
          >
            {savingPlayer ? t('lobby.loading') : t('lobby.save')}
          </button>
        </div>
      )}
    </div>
  )
}

PlayerProfile.propTypes = {
  currentPlayer: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    alias: PropTypes.string,
    email: PropTypes.string,
    game_role: PropTypes.string,
    avatar: PropTypes.string
  }),
  currentTeam: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lobby_ready: PropTypes.bool,
    players: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        email: PropTypes.string,
        game_role: PropTypes.string
      })
    )
  }),
  takenRolesExcludingSelf: PropTypes.arrayOf(PropTypes.string),
  isGameCreator: PropTypes.bool
}

PlayerProfile.defaultProps = {
  currentPlayer: null,
  currentTeam: null,
  takenRolesExcludingSelf: [],
  isGameCreator: false
}

export default PlayerProfile
